import type { HistorySupplier, SupplierRow } from './review';

// "All suppliers" on screen 17: registered suppliers and those known only from the history
// import (D69), in one list. Pure, so the page and the tests share it.

export type AllStatus = 'approved' | 'submitted' | 'rejected' | 'history';
export const ALL_STATUSES: readonly AllStatus[] = ['approved', 'submitted', 'rejected', 'history'];

export interface AllSupplier {
  supplierId: string;
  name: string;
  /** Location for registered suppliers, industry for history ones. */
  sub: string;
  categories: string[];
  status: AllStatus;
}

const ORDER: Record<AllStatus, number> = { approved: 0, submitted: 1, rejected: 2, history: 3 };

export function mergeSuppliers(
  registered: readonly SupplierRow[],
  history: readonly HistorySupplier[],
  place: (s: SupplierRow) => string,
): AllSupplier[] {
  const out: AllSupplier[] = [];
  const seen = new Set<string>();
  for (const s of registered) {
    if (s.status !== 'approved' && s.status !== 'submitted' && s.status !== 'rejected') continue;
    seen.add(s.supplierId);
    out.push({
      supplierId: s.supplierId,
      name: s.name,
      sub: place(s),
      categories: s.categories,
      status: s.status,
    });
  }
  for (const h of history) {
    if (seen.has(h.supplierId)) continue;
    out.push({
      supplierId: h.supplierId,
      name: h.name,
      sub: h.industry,
      categories: h.categories,
      status: 'history',
    });
  }
  return out.sort((a, b) => ORDER[a.status] - ORDER[b.status] || a.name.localeCompare(b.name));
}

/** Search on name, the sub line and category names; `status` '' means every status. */
export function filterSuppliers(
  rows: readonly AllSupplier[],
  query: string,
  status: AllStatus | '',
  catName: (id: string) => string,
): AllSupplier[] {
  const needle = query.trim().toLowerCase();
  return rows.filter(
    (s) =>
      (!status || s.status === status) &&
      (!needle ||
        s.name.toLowerCase().includes(needle) ||
        s.sub.toLowerCase().includes(needle) ||
        s.categories.some((c) => catName(c).toLowerCase().includes(needle))),
  );
}
